<template>
    <main v-if="user" class="flex justify-center bg-gray-50">
      <HeaderComponent />
      <section class="w-full pb-[100px] lg:pl-[50px] lg:pt-[95px] pt-[95px] max-h-screen overflow-y-auto relative flex justify-start flex-col lg:p-[25px] p-[20px] h-auto min-h-screen">
        <!-- Notifications -->
        <div class="fixed top-[80px] lg:left-[calc(50%+140px)] left-[50%] z-[100] translate-x-[-50%] w-[90%] max-w-[400px] flex flex-col">
            <div v-if="errorMessage" class="w-full rounded-lg text-white bg-red-500 my-2 font-medium text-center p-4 shadow-lg transition-all duration-300">
                {{ errorMessage }}
            </div>
            <div v-if="successMessage" class="w-full rounded-lg text-white bg-emerald-500 my-2 font-medium text-center p-4 shadow-lg transition-all duration-300">
                {{ successMessage }}
            </div>
        </div>

        <!-- Main content -->
        <div class="w-full flex flex-col">
          <!-- Filters and search -->
          <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
            <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <!-- Filter tabs -->
              <div class="flex overflow-x-auto pb-2 md:pb-0">
                <button 
                  :class="{'border-b-2 border-orange-500 text-orange-500': currentFilter === 'all'}" 
                  @click="setFilter('all')" 
                  class="text-sm font-medium text-gray-500 hover:text-orange-500 px-3 py-2 whitespace-nowrap"
                >
                  Toutes les commandes
                </button>
                <button 
                  v-if="user.isSuper" 
                  :class="{'border-b-2 border-orange-500 text-orange-500': currentFilter === 'store'}" 
                  @click="setFilter('store')" 
                  class="text-sm font-medium text-gray-500 hover:text-orange-500 px-3 py-2 whitespace-nowrap"
                >
                  Boutique
                </button>
                <button 
                  v-if="user.isSuper" 
                  :class="{'border-b-2 border-orange-500 text-orange-500': currentFilter === 'Calavi'}"
                  @click="setFilter('Calavi')"
                  class="text-sm font-medium text-gray-500 hover:text-orange-500 px-3 py-2 whitespace-nowrap"
                >
                  Calavi
                </button>
                <button 
                  v-if="user.isSuper" 
                  :class="{'border-b-2 border-orange-500 text-orange-500': currentFilter === 'Parakou'}" 
                  @click="setFilter('Parakou')" 
                  class="text-sm font-medium text-gray-500 hover:text-orange-500 px-3 py-2 whitespace-nowrap"
                >
                  Parakou
                </button>
                <button 
                  v-if="user.isSuper" 
                  :class="{'border-b-2 border-orange-500 text-orange-500': currentFilter === 'En ligne'}" 
                  @click="setFilter('En ligne')" 
                  class="text-sm font-medium text-gray-500 hover:text-orange-500 px-3 py-2 whitespace-nowrap"
                >
                  En ligne
                </button>
              </div>
              
              <!-- Report and date filter -->
              <div class="flex items-center gap-3">
                <button 
                  v-if="user.isSuper" 
                  @click="openModal" 
                  class="bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium py-2 px-3 rounded-lg hidden md:flex items-center"
                >
                  <i class="fas fa-file-excel mr-2"></i>
                  Générer rapport
                </button>
                
                <div class="relative">
                  <input 
                    type="date" 
                    v-model="selectedDate" 
                    @change="fetchSales" 
                    class="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                  >
                </div>
              </div>
            </div>
            
            <!-- Search bar -->
            <div class="mt-4 relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i class="fas fa-search text-gray-400"></i>
              </div>
              <input 
                type="text" 
                v-model="searchQuery" 
                @input="debouncedSearch" 
                placeholder="Rechercher par produit, IMEI ou client..." 
                class="pl-10 w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
              >
            </div>
            
            <!-- Total summary -->
            <div class="mt-4 rounded-lg">
              <!-- Total validé - plus visible -->
              <div class="flex justify-between items-center mb-2 p-2 bg-white rounded-lg">
                <div>
                  <span class="text-sm font-medium text-gray-700">
                    Total {{ filterLabel }} validées:
                  </span>
                  <span class="text-sm font-semibold text-green-600 ml-1">
                    {{ validSalesCount }} ventes
                  </span>
                </div>
                <span class="md:text-lg text-sm font-bold text-green-600">
                  {{ totalAmount?.toLocaleString() }} FCFA
                </span>
              </div>
            
              <!-- Total non validé -->
              <div v-if="totalSales > validSalesCount" class="flex justify-between items-center mb-2 p-2 bg-white rounded-lg">
                <div>
                  <span class="text-sm font-medium text-gray-700">
                    Total {{ filterLabel }} non validées:
                  </span>
                  <span class="text-sm font-semibold text-blue-600 ml-1">
                    {{ totalSales - validSalesCount }} ventes
                  </span>
                </div>
                <span class="md:text-lg text-sm font-bold text-blue-600">
                  {{ (totalAllAmount - totalAmount).toLocaleString() }} FCFA
                </span>
              </div>
            
              <!-- Total général -->
              <div class="flex justify-between items-center p-2 bg-orange-50 rounded-lg">
                <div>
                  <span class="text-sm font-medium text-gray-700">
                    Total {{ filterLabel }} général:
                  </span>
                  <span class="text-sm font-semibold text-orange-600 ml-1">
                    {{ totalSales }} ventes
                  </span>
                </div>
                <span class="md:text-lg text-sm font-bold text-orange-600">
                  {{ totalAllAmount?.toLocaleString() }} FCFA
                </span>
              </div>
            </div>
          </div>
          
          <!-- Report modal -->
          <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" style="left: 280px;">
            <div class="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
              <div class="p-6">
                <div class="flex justify-between items-center mb-4">
                  <h2 class="text-xl font-bold text-gray-800">Générer un rapport</h2>
                  <button @click="closeModal" class="text-gray-400 hover:text-gray-600">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
                
                <form @submit.prevent="generateReport">
                  <div class="mb-4">
                    <label for="startDate" class="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
                    <input 
                      type="date" 
                      id="startDate" 
                      v-model="startDate" 
                      class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                  
                  <div class="mb-6">
                    <label for="endDate" class="block text-sm font-medium text-gray-700 mb-1">Date de fin</label>
                    <input 
                      type="date" 
                      id="endDate" 
                      v-model="endDate" 
                      class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                  
                  <div class="flex justify-end space-x-3">
                    <button 
                      type="button" 
                      @click="closeModal" 
                      class="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium text-sm"
                    >
                      Annuler
                    </button>
                    <button 
                      type="submit" 
                      class="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium text-sm flex items-center"
                    >
                      <i class="fas fa-file-excel mr-2"></i>
                      Générer
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          
          <!-- Sales table -->
          <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div class="overflow-x-auto parent">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th scope="col" class="md:px-6 px-[10px] py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Client</th>
                    <th scope="col" class="md:px-6 px-[10px] py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Produits</th>
                    <th scope="col" class="md:px-6 px-[10px] py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Type</th>
                    <th scope="col" class="md:px-6 px-[10px] py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th scope="col" class="md:px-6 px-[10px] py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"><span class="md:flex hidden">Statut</span><span class="md:hidden">Sta</span></th>
                    <th scope="col" class="md:px-6 px-[10px] py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    <th scope="col" class="md:px-6 px-[10px] py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"><span class="md:flex hidden">Actions</span><span class="md:hidden flex">Act</span></th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr 
                    v-for="(history, index) in sales" 
                    :key="index" 
                    class="hover:bg-gray-50" :class="{'bg-orange-50 hover:bg-orange-100': history.type === 'Troc'}"
                  >
                    <!-- Client column -->
                    <td class="md:px-6 px-[10px] py-4 hidden md:table-cell">
                      <div class="flex items-center">
                        <div class="flex-shrink-0 h-10 w-10 rounded-full overflow-hidden border-2 border-white shadow-sm">
                          <img 
                            v-if="history.vendeur.boutique === 'Calavi'"
                            src="../assets/images/calavi.jpg" 
                            class="h-full w-full object-cover"
                          >
                          <img 
                            v-if="history.vendeur.boutique === 'Parakou'"
                            src="../assets/images/calavi.jpg" 
                            class="h-full w-full object-cover"
                          >
                          <div 
                            v-if="history.vendeur.boutique === 'En ligne'" 
                            class="h-full w-full bg-blue-100 flex items-center justify-center text-blue-500"
                          >
                            <i class="fas fa-globe text-lg"></i>
                          </div>
                        </div>
                        <div class="ml-4 hidden md:block">
                          <div class="text-sm font-medium text-gray-900">
                            {{ history.client?.prenoms }} {{ history.client?.nom }}
                          </div>
                          <div class="text-xs text-gray-500">
                            <span v-if="history.vendeur.boutique !== 'En ligne'">
                              {{ history.vendeur?.prenoms }} {{ history.vendeur?.nom }} - 
                            </span>
                            {{ history.vendeur.boutique }}
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    <!-- Products column -->
                    <td class="md:px-6 px-[10px] py-4">
                      <div class="text-sm text-gray-900 max-w-xs">
                        {{ history.produits.map(p => `${p.lotName} (${p.quantity})`).join(', ') }}
                      </div>
                      <div class="text-xs text-gray-500 mt-1">
                        IMEIs: {{ history.produits.filter(p => p.imei1).map(p => p.imei1).join(', ') }}
                      </div>
                    </td>
                    
                    <!-- Type column -->
                    <td class="md:px-6 px-[10px] py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                      {{ history.type }}
                    </td>
                    
                    <!-- Payment column -->
                    <td class="md:px-6 px-[10px] py-4 whitespace-wrap text-sm text-gray-500">
                      {{ formatDate(history.createdAt) }}
                    </td>
                    
                    <!-- Status column -->
                    <td class="md:px-6 px-[10px] py-4 relative md:flex items-center justify-start whitespace-nowrap">
                      <span 
                        class="px-2 py-1 text-xs w-auto relative font-semibold hidden md:flex rounded-full"
                        :class="{'bg-green-100 text-green-800': history.isValide, 'bg-blue-100 text-blue-800': !history.isValide}"
                      >
                        {{ history.isValide ? 'Validé' : 'En attente' }}
                      </span>
                      <span 
                        class="w-[18px] h-[18px] min-w-[18px] md:hidden min-h-[18px] rounded-full flex items-center justify-center text-xs text-white"
                        :class="{'bg-green-800': history.isValide, 'bg-blue-800': !history.isValide}"
                      >
                      {{ history.produits.length }}
                      </span>
                    </td>
                    
                    <!-- Total column -->
                    <td class="md:px-6 px-[10px] py-4 md:text-sm text-[12px] font-semibold text-gray-900">
                      {{ history.totalApresReduction }} <span> FCFA</span>
                    </td>
                    
                    <!-- Actions column -->
                    <td class="md:px-6 px-[10px] py-4 text-center text-sm font-medium">
                      <button 
                        @click="toggleBox(history._id)" 
                        class="text-gray-500 hover:text-orange-500"
                      >
                        <i class="fas fa-ellipsis-v"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
              
              <!-- Empty state -->
              <div 
                v-if="sales.length === 0 && !isLoading" 
                class="text-center py-12"
              >
                <i class="fas fa-box-open text-4xl text-gray-300 mb-3"></i>
                <p class="text-gray-500">Aucune vente trouvée</p>
              </div>
              
              <!-- Loading state -->
              <div 
                v-if="isLoading" 
                class="flex justify-center items-center py-12"
              >
                <div class="spinner"></div>
              </div>
            </div>
            
            <!-- Pagination -->
            <div v-if="totalPages > 1" class="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div class="flex-1 flex justify-between sm:hidden">
                <button 
                  @click="prevPage" 
                  :disabled="currentPage === 1" 
                  :class="{'opacity-50 cursor-not-allowed': currentPage === 1}"
                  class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Précédent
                </button>
                <button 
                  @click="nextPage" 
                  :disabled="currentPage === totalPages" 
                  :class="{'opacity-50 cursor-not-allowed': currentPage === totalPages}"
                  class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Suivant
                </button>
              </div>
              <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p class="text-sm text-gray-700">
                    Affichage de <span class="font-medium">{{ (currentPage - 1) * 20 + 1 }}</span> à 
                    <span class="font-medium">{{ Math.min(currentPage * 20, totalSales) }}</span> sur 
                    <span class="font-medium">{{ totalSales }}</span> résultats
                  </p>
                </div>
                <div>
                  <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button 
                      @click="prevPage" 
                      :disabled="currentPage === 1" 
                      :class="{'opacity-50 cursor-not-allowed': currentPage === 1}"
                      class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    >
                      <span class="sr-only">Précédent</span>
                      <i class="fas fa-chevron-left"></i>
                    </button>
                    <button 
                      v-for="page in visiblePages" 
                      :key="page"
                      @click="goToPage(page)"
                      :class="{'z-10 bg-orange-50 border-orange-500 text-orange-600': currentPage === page, 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50': currentPage !== page}"
                      class="relative inline-flex items-center px-4 py-2 border text-sm font-medium"
                    >
                      {{ page }}
                    </button>
                    <button 
                      @click="nextPage" 
                      :disabled="currentPage === totalPages" 
                      :class="{'opacity-50 cursor-not-allowed': currentPage === totalPages}"
                      class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    >
                      <span class="sr-only">Suivant</span>
                      <i class="fas fa-chevron-right"></i>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Sale details modal -->
        <div v-if="showBoxId" class="fixed inset-0 bg-black bg-opacity-50 lg:left-[280px] flex items-center justify-center z-50">
          <div class="bg-white rounded-xl shadow-xl w-full parent max-w-4xl max-h-[90vh] overflow-y-auto mx-4">
            <div class="p-6">
              <div class="flex justify-between items-center mb-4">
                <h2 class="text-xl font-bold text-gray-800">Détails de la vente</h2>
                <button @click="toggleBox(null)" class="text-gray-400 hover:text-gray-600">
                  <i class="fas fa-times"></i>
                </button>
              </div>

              <!-- Payment and validation -->
              <div class="grid grid-cols-1 md:grid-cols-2 mb-6 gap-6">
                <!-- Payment summary -->
                <div class="bg-gray-50 p-4 rounded-lg">
                  <h3 class="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                    <i class="fas fa-money-bill-wave mr-2 text-orange-500"></i>
                    Paiement
                  </h3>
                  <div class="space-y-2">
                    <div class="flex justify-between">
                      <span class="text-sm text-gray-600">Moyen de paiement:</span>
                      <span class="text-sm font-medium">{{ selectedSale.moyenDePaiement }}</span>
                    </div>
                    <div class="flex justify-between">
                      <span class="text-sm text-gray-600">Type de vente:</span>
                      <span class="text-sm font-medium">{{ selectedSale.type }}</span>
                    </div>
                    <div class="flex justify-between">
                      <span class="text-sm text-gray-600">Date:</span>
                      <span class="text-sm font-medium">{{ new Date(selectedSale.createdAt).toLocaleString() }}</span>
                    </div>
                  </div>
                </div>

                <!-- Total and actions -->
                <div class="bg-orange-50 p-4 rounded-lg flex flex-col justify-between">
                  <div class="flex justify-between items-center mb-4">
                    <h3 class="text-lg font-semibold text-gray-800">Total</h3>
                    <span class="text-xl font-bold text-orange-600">{{ selectedSale.totalApresReduction }} FCFA</span>
                  </div>

                  <div class="flex space-x-3">
                    <button 
                      v-if="user.isSuper && !selectedSale.isValide" 
                      @click="toggleActive(selectedSale._id)" 
                      class="flex-1 bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg font-medium text-sm"
                    >
                      Valider
                    </button>
                    <button 
                      v-if="user.isSuper && selectedSale.isValide" 
                      @click="toggleActive(selectedSale._id)" 
                      class="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg font-medium text-sm"
                    >
                      Rejeter
                    </button>
                    <button 
                      v-if="user.isSuper && !selectedSale.isValide" 
                      @click="deleteHistorique(selectedSale._id)" 
                      class="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded-lg font-medium text-sm flex items-center justify-center"
                    >
                      <i class="fas fa-trash mr-2"></i>
                      Supprimer
                    </button>
                  </div>
                </div>
              </div>
            
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <!-- Client details -->
                <div class="bg-gray-50 p-4 rounded-lg">
                  <h3 class="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                    <i class="fas fa-user-circle mr-2 text-orange-500"></i>
                    Informations client
                  </h3>
                  <div class="space-y-2">
                    <div class="flex items-center">
                      <i class="fas fa-user mr-2 text-gray-500 w-5"></i>
                      <span class="text-sm">{{ selectedSale.client?.nom }} {{ selectedSale.client?.prenoms }}</span>
                    </div>
                    <div class="flex items-center">
                      <i class="fas fa-envelope mr-2 text-gray-500 w-5"></i>
                      <span class="text-sm">{{ selectedSale.client?.email || 'Non renseigné' }}</span>
                    </div>
                    <div class="flex items-center">
                      <i class="fas fa-phone mr-2 text-gray-500 w-5"></i>
                      <span class="text-sm">{{ selectedSale.client?.numero }}</span>
                    </div>
                    <div class="flex items-center">
                      <i class="fas fa-map-marker-alt mr-2 text-gray-500 w-5"></i>
                      <span class="text-sm">{{ selectedSale.client?.adresse || 'Non renseigné' }}</span>
                    </div>
                  </div>
                </div>

                <!-- Seller details -->
                <div class="bg-gray-50 p-4 rounded-lg">
                  <h3 class="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                    <i class="fas fa-store mr-2 text-orange-500"></i>
                    Informations vendeur
                  </h3>
                  <div class="space-y-2">
                    <div class="flex items-center">
                      <i class="fas fa-user mr-2 text-gray-500 w-5"></i>
                      <span class="text-sm">{{ selectedSale.vendeur?.prenoms }} {{ selectedSale.vendeur?.nom }}</span>
                    </div>
                    <div class="flex items-center">
                      <i class="fas fa-envelope mr-2 text-gray-500 w-5"></i>
                      <span class="text-sm">{{ selectedSale.vendeur?.email }}</span>
                    </div>
                    <div class="flex items-center">
                      <i class="fas fa-phone mr-2 text-gray-500 w-5"></i>
                      <span class="text-sm">{{ selectedSale.vendeur?.numero }}</span>
                    </div>
                    <div class="flex items-center">
                      <i class="fas fa-briefcase mr-2 text-gray-500 w-5"></i>
                      <span class="text-sm">{{ selectedSale.vendeur?.poste }}</span>
                    </div>
                    <div class="flex items-center">
                      <i class="fas fa-map-marker-alt mr-2 text-gray-500 w-5"></i>
                      <span class="text-sm">{{ selectedSale.vendeur?.boutique }}</span>
                    </div>
                  </div>
                </div>
              </div>
            
              <!-- Troc information -->
              <div v-if="selectedSale.type === 'Troc' && selectedSale.troc" class="mb-6">
                <h3 class="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <i class="fas fa-exchange-alt mr-2 text-orange-500"></i>
                  Produit troqué
                </h3>
                <div class="rounded-lg border border-yellow-200">
                  <div class="flex items-start p-3 border border-yellow-300 rounded-lg bg-white">
                    <div class="flex-shrink-0 h-12 w-12 rounded overflow-hidden border-2 border-white shadow-sm mr-3">
                      <img 
                        v-if="selectedSale.vendeur.boutique === 'Calavi'"
                        src="../assets/images/calavi.jpg" 
                        class="h-full w-full object-cover"
                      >
                      <img 
                        v-if="selectedSale.vendeur.boutique === 'Parakou'"
                        src="../assets/images/calavi.jpg" 
                        class="h-full w-full object-cover"
                      >
                      <div 
                        v-if="selectedSale.vendeur.boutique === 'En ligne'" 
                        class="h-full w-full bg-blue-100 flex items-center justify-center text-blue-500"
                      >
                        <i class="fas fa-globe text-lg"></i>
                      </div>
                    </div>
                    <div class="flex-grow">
                      <div class="flex justify-between flex-wrap">
                        <h4 class="text-sm font-medium text-gray-900">{{ selectedSale.troc.lotName }}</h4>
                        <span class="text-sm font-semibold">Prix d'achat : {{ selectedSale.troc.stotal }} FCFA | Prix de revente : {{ selectedSale.troc.prix }} FCFA</span>
                      </div>
                      <div class="text-xs text-gray-500 mt-1">
                        <span> Quantité: {{ selectedSale.troc.quantity }}</span>
                      </div>
                      <div class="text-xs text-gray-500 mt-1" v-if="selectedSale.troc.imei1 || selectedSale.troc.imei2">
                        <span v-if="selectedSale.troc.imei1">IMEI1: {{ selectedSale.troc.imei1 }}</span>
                        <span v-if="selectedSale.troc.imei2"> | IMEI2: {{ selectedSale.troc.imei2 }}</span>
                      </div>
                      <div class="text-xs text-yellow-600 font-medium mt-1">
                        <i class="fas fa-info-circle mr-1"></i>
                        Produit donné en échange
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Products list -->
              <div class="">
                <h3 class="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <i class="fas fa-boxes mr-2 text-orange-500"></i>
                  Produits vendus
                </h3>
                <div class="space-y-3">
                  <div 
                    v-for="(produit, index) in selectedSale.produits" 
                    :key="index" 
                    class="flex items-start p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div class="flex-shrink-0 h-12 w-12 rounded overflow-hidden border-2 border-white shadow-sm mr-3">
                      <img 
                        v-if="selectedSale.vendeur.boutique === 'Calavi'"
                        src="../assets/images/calavi.jpg" 
                        class="h-full w-full object-cover"
                      >
                      <img 
                        v-if="selectedSale.vendeur.boutique === 'Parakou'"
                        src="../assets/images/calavi.jpg" 
                        class="h-full w-full object-cover"
                      >
                      <div 
                        v-if="selectedSale.vendeur.boutique === 'En ligne'" 
                        class="h-full w-full bg-blue-100 flex items-center justify-center text-blue-500"
                      >
                        <i class="fas fa-globe text-lg"></i>
                      </div>
                    </div>
                    <div class="flex-grow">
                      <div class="flex justify-between">
                        <h4 class="text-sm font-medium text-gray-900">{{ produit.lotName }}</h4>
                        <span class="text-sm font-semibold">{{ produit.stotal }} FCFA</span>
                      </div>
                      <div class="text-xs text-gray-500 mt-1">
                        <span v-if="produit.capacities">{{ produit.capacities[0] }}</span>
                        <span v-if="produit.colors"> | {{ produit.colors[0] }}</span>
                        <span> | Quantité: {{ produit.quantity }}</span>
                      </div>
                      <div class="text-xs text-gray-500 mt-1" v-if="produit.imei1 || produit.imei2">
                        <span v-if="produit.imei1">IMEI1: {{ produit.imei1 }}</span>
                        <span v-if="produit.imei2"> | IMEI2: {{ produit.imei2 }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    </main>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import axios from 'axios';
import { debounce } from 'lodash';
import HeaderComponent from '../components/HeaderComponent.vue';
import { useAuthStore } from '@/store/auth';

const authStore = useAuthStore();
const user = computed(() => authStore.user);

// State management
const showBoxId = ref(null);
const currentFilter = ref('all');
const sales = ref([]);
const selectedDate = ref(null);
const errorMessage = ref('');
const successMessage = ref('');
const isLoading = ref(false);
const searchQuery = ref('');
const showModal = ref(false);
const startDate = ref('');
const endDate = ref('');

// Pagination
const currentPage = ref(1);
const totalPages = ref(1);
const totalSales = ref(0);
const validSalesCount = ref(0);
const totalAmount = ref(0);
const totalAllAmount = ref(0);

// Computed properties
const filterLabel = computed(() => {
  switch(currentFilter.value) {
    case 'Calavi': return 'Calavi';
    case 'Parakou': return 'Parakou';
    case 'En ligne': return 'En ligne';
    case 'store': return 'Boutique';
    default: return 'global';
  }
});

function formatDate(dateString) {
  if (!dateString) return ""

  const date = new Date(dateString)
  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).replace(",", " à")
}

const selectedSale = computed(() => {
  return sales.value.find(s => s._id === showBoxId.value) || {};
});

const visiblePages = computed(() => {
  const pages = [];
  const maxVisible = 5;
  let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages.value, start + maxVisible - 1);
  
  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1);
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  
  return pages;
});

// Methods
function toggleBox(id) {
  showBoxId.value = showBoxId.value === id ? null : id;
}

function setFilter(filter) {
  currentFilter.value = filter;
  currentPage.value = 1;
  fetchSales();
}

async function fetchSales() {
  isLoading.value = true;
  try {
    const params = {
      page: currentPage.value,
      limit: 20,
      search: searchQuery.value,
      date: selectedDate.value,
      isSuper: user.value.isSuper.toString(),
      userId: user.value._id
    };

    if (user.value.isSuper && currentFilter.value !== 'all') {
      params.boutique = currentFilter.value;
    }

    // Utilisez le même objet params pour les deux requêtes
    const [salesResponse, statsResponse] = await Promise.all([
      axios.get('/api/sales', { params }),
      axios.get('/api/sales/history/stats', { params })
    ]);

    sales.value = salesResponse.data.sales;
    totalPages.value = salesResponse.data.totalPages;
    totalSales.value = salesResponse.data.total;
    
    // Assurez-vous que le backend renvoie bien ces données
    validSalesCount.value = salesResponse.data.stats.validSalesCount;
    totalAmount.value = salesResponse.data.stats.totalAmount;
    totalAllAmount.value = salesResponse.data.stats.totalAllAmount;
    
  } catch (error) {
    errorMessage.value = 'Erreur lors du chargement des ventes';
    hideErrorMessageAfterDelay();
  } finally {
    isLoading.value = false;
  }
}

function scrollToTop() {
  const mainSection = document.querySelector('section');
  if (mainSection) {
    mainSection.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  } else {
    // Fallback au scroll window si la section n'est pas trouvée
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
}

watch(searchQuery, (newVal) => {
  if (newVal) {
    debouncedSearch();
  }
});

const debouncedSearch = debounce(() => {
  currentPage.value = 1;
  fetchSales();
}, 200);

function nextPage() {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
    fetchSales();
    scrollToTop();
  }
}

function prevPage() {
  if (currentPage.value > 1) {
    currentPage.value--;
    fetchSales();
    scrollToTop();
  }
}

function goToPage(page) {
  currentPage.value = page;
  fetchSales();
    scrollToTop();
}

async function deleteHistorique(id) {
  try {
    await axios.delete(`/api/sales/${id}`);
    await fetchSales();
    showBoxId.value = null;
    successMessage.value = 'Vente supprimée avec succès';
    hideSuccessMessageAfterDelay();
  } catch (error) {
    errorMessage.value = 'Erreur lors de la suppression de la vente';
    hideErrorMessageAfterDelay();
  }
}

async function toggleActive(id) {
  try {
    await axios.put(`/api/sales/${id}/toggle-active`);
    await fetchSales();
    showBoxId.value = null;
    successMessage.value = 'Statut de la vente mis à jour';
    hideSuccessMessageAfterDelay();
  } catch (error) {
    errorMessage.value = error.response?.data?.error || 'Erreur lors du changement de statut';
    hideErrorMessageAfterDelay();
  }
}

function openModal() {
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
}

async function generateReport() {
  try {
    const response = await axios.post('/api/export-sales', {
      startDate: startDate.value,
      endDate: endDate.value,
      boutique: currentFilter.value === 'all' ? null : currentFilter.value
    }, {
      responseType: 'blob'
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `rapport_ventes_${startDate.value}_à_${endDate.value}.xlsx`);
    document.body.appendChild(link);
    link.click();

    closeModal();
  } catch (error) {
    errorMessage.value = 'Erreur lors de la génération du rapport';
    hideErrorMessageAfterDelay();
  }
}

function hideSuccessMessageAfterDelay() {
  setTimeout(() => {
    successMessage.value = ''; 
  }, 5000);
}

function hideErrorMessageAfterDelay() {
  setTimeout(() => {
    errorMessage.value = ''; 
  }, 10000);
}

// Watchers
//watch(currentPage, fetchSales);
watch(selectedDate, fetchSales);

// Lifecycle hooks
onMounted(fetchSales);
</script>

<style scoped>
.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: rgb(234, 88, 12);
  animation: spin 1s ease infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease-out;
}

.slide-up-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.slide-up-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>